import BaseModel from "../../../src/models/BaseModel";
import ObjectFactory from "../../../src/models/ObjectFactory";
import Point from "../../../src/models/Point";
import type { C1_PointData, C1_WallData } from "../types/C1_ModelTypes";
import type { C1_Room } from "./C1_Room";
import { wallMergeRooms } from "./C1_mergeRooms";
import type { C1_Floor } from "./C1_Floor";

type WallMoveDescriptor = {
  room: C1_Room;
  wallId: string;
  side: ReturnType<C1_Room["compatibilitySide"]>;
  segmentStart: number;
  segmentEnd: number;
  targetCoordinate: number;
  needsConnectors: boolean;
};

/** Persistent room boundary segment, or an independent flagged segment owned by a floor. */
export class C1_Wall extends BaseModel {
  private _start = new Point(0, 0);
  private _freeWall = false;
  private _end = new Point(0, 0);
  private _visible = true;
  private _exterior?: boolean;
  private _partnerWallId?: string;

  get className(): string {
    return "C1_Wall";
  }

  getParentCollectionName(): string {
    return this.freeWall ? "freeWalls" : "walls";
  }

  override canBeDeletedInteractive(): boolean {
    return this.freeWall || wallMergeRooms(this) !== undefined;
  }

  override delete(withoutUpdate?: boolean): ReturnType<BaseModel["delete"]> {
    if (this.freeWall) {
      const floor = this.parent?.c1 as C1_Floor | undefined;
      for (const room of floor?.rooms ?? []) for (const item of room.furnitures) {
        if (item.dockWall === this) item.setPosition(item.relativePosition, undefined, item.reflect, undefined);
      }
      for (const room of floor?.rooms ?? []) for (const item of room.interactiveEloItems ?? []) {
        const placement = item.data.interactivePlacement;
        if (placement?._wallId === this.id) item.setInteractivePlacement({ ...placement, side: undefined, wallDocked: undefined, _wallId: undefined });
      }
    }
    return super.delete(withoutUpdate);
  }

  override getPersistentProperties(): string[] {
    return ["start", "_freeWall", "end", "_visible", "_exterior", "_partnerWallId", ...super.getPersistentProperties()];
  }

  constructor(data: Partial<C1_WallData> = {}, parent?: BaseModel) {
    super(undefined, parent);
    this._freeWall = data.freeWall ?? false;
    if (data.start) this._start.fromJSON(data.start);
    if (data.end) this._end.fromJSON(data.end);
    this._visible = data.visible ?? true;
    this._exterior = data.exterior ?? !this._freeWall;
    this._partnerWallId = data.partnerWallId;
  }

  /** Moves a wall and its physical partner according to each owning room's polygon topology. */
  static moveSegmentToCoordinate(room: C1_Room, wall: C1_Wall, targetCoordinate: number): void {
    const moves: WallMoveDescriptor[] = [];
    const sourceMove = C1_Wall.createMoveDescriptor(room, wall, targetCoordinate);
    if (sourceMove) moves.push(sourceMove);

    const partnerWall = wall.partnerWall;
    const partnerRoom = partnerWall?.parent?.c1 as C1_Room | undefined;
    if (partnerWall && partnerRoom?.className === "C1_Room") {
      const partnerMove = C1_Wall.createMoveDescriptor(partnerRoom, partnerWall, targetCoordinate);
      if (partnerMove) moves.push(partnerMove);
    }

    for (const move of moves) C1_Wall.applyMoveDescriptor(move);
    room.recalculateFloorWallTypes();
  }

  /** Captures all geometry needed to move a segment before either room is modified. */
  private static createMoveDescriptor(
    room: C1_Room,
    wall: C1_Wall,
    targetCoordinate: number,
  ): WallMoveDescriptor | undefined {
    const points = room.wallPoints(wall);
    if (!points) return undefined;
    const side = room.compatibilitySide(wall);
    const vertical = side === "left" || side === "right";
    return {
      room,
      wallId: wall.id,
      side,
      segmentStart: vertical ? points.start.y : points.start.x,
      segmentEnd: vertical ? points.end.y : points.end.x,
      targetCoordinate,
      needsConnectors: room.wallHasCollinearNeighbor(wall),
    };
  }

  /** Applies a captured move either directly or by inserting perpendicular connector segments. */
  private static applyMoveDescriptor(move: WallMoveDescriptor): void {
    const wall = move.room.walls?.findFirst((candidate) => candidate.id === move.wallId);
    if (!wall) return;
    if (move.needsConnectors) {
      move.room.extrudeWallSegment(
        move.wallId,
        move.side,
        move.segmentStart,
        move.segmentEnd,
        move.targetCoordinate,
      );
      return;
    }
    const kind = move.side === "left" || move.side === "right" ? "v" : "h";
    move.room.moveWallToCoordinate(wall, kind, move.targetCoordinate);
  }

  get end(): Readonly<C1_PointData> {
    return this._end;
  }

  get freeWall(): boolean { return this._freeWall; }

  get start(): Readonly<C1_PointData> { return this._start; }

  /** Sets independent endpoints without moving room vertices or generating axes. */
  setEndpoints(start: C1_PointData, end: C1_PointData): void {
    if (!this.freeWall) throw new Error("Nur freie Wände besitzen unabhängige Endpunkte.");
    if (![start.x, start.y, end.x, end.y].every(Number.isFinite) || Math.hypot(end.x - start.x, end.y - start.y) < 0.01)
      throw new Error("Eine freie Wand muss mindestens einen Zentimeter lang sein.");
    if (this._start.x !== start.x || this._start.y !== start.y) {
      this.beforeChange("start");
      this._start = new Point(start.x, start.y);
      this.changed("start");
    }
    this.setEnd(end);
  }

  get visible(): boolean {
    return this._visible;
  }

  get exterior(): boolean | undefined {
    return this._exterior;
  }

  /** Matching segment owned by the room on the other side of an interior wall. */
  get partnerWall(): C1_Wall | undefined {
    return state.objectRegistry.getObject(this._partnerWallId ?? "") as C1_Wall | undefined;
  }

  set partnerWall(partnerWall: C1_Wall | undefined) {
    const partnerWallId = partnerWall?.id;
    if (this._partnerWallId === partnerWallId) return;
    this.beforeChange("partnerWall");
    this._partnerWallId = partnerWallId;
    this.changed("partnerWall");
  }

  setEnd(end: C1_PointData): void {
    if (this._end.x === end.x && this._end.y === end.y) return;
    this.beforeChange("end");
    this._end = new Point(end.x, end.y);
    this.changed("end");
  }

  /** Uniform coordinate correction, preserving even very short existing segments. */
  rescaleCoordinates(factor: number): void {
    if (!Number.isFinite(factor) || factor <= 0) throw new Error("Ungültiger Maßstab.");
    if (this.freeWall) {
      this.beforeChange("start");
      this._start = new Point(this._start.x * factor, this._start.y * factor);
      this.changed("start");
    }
    this.setEnd({ x: this.end.x * factor, y: this.end.y * factor });
  }

  setVisible(visible: boolean): void {
    this.setOwnVisibility(visible);
    this.partnerWall?.setOwnVisibility(visible);
  }

  /** Updates this wall segment without recursively touching its partner. */
  private setOwnVisibility(visible: boolean): void {
    if (this._visible === visible) return;
    this.beforeChange("visible");
    this._visible = visible;
    this.changed("visible");
  }

  setExterior(exterior?: boolean): void {
    if (this._exterior === exterior) return;
    this.beforeChange("exterior");
    this._exterior = exterior;
    this.changed("exterior");
  }
}

ObjectFactory.registerClass("C1_Wall", (data, parent) => new C1_Wall(data, parent));
